import { Injectable } from '@angular/core';

export interface CountryDateFormats {
  displayDateFormatAngular: string;
  calendarDateFormat: string;
  displayDateTimeFormatMoment: string;
  shortDateTimeFormatMoment: string;
  flatpickrDateFormat: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {

  private readonly dayFirstCountries = new Set<string>([
    'AU', 'NZ', 'GB', 'IE', 'ZA', 'FR', 'DE', 'IT', 'ES', 'SE', 'FI', 'NO', 'NL', 'BE', 'DK', 'CH',
    'AT', 'PT', 'BR', 'IN', 'MX', 'AR', 'CL', 'CO', 'PE', 'SG', 'HK'
  ]);

  // ISO YYYY-MM-DD countries (Canada's official government standard; matches
  // the backend's _amt_dayview_get_short_date_format year-first grouping).
  private readonly yearFirstCountries = new Set<string>([
    'CA'
  ]);

  private readonly countryCurrencyMap: { [key: string]: string } = {
    'US': 'USD', 'CA': 'CAD', 'GB': 'GBP', 'AU': 'AUD', 'NZ': 'NZD',
    'JP': 'JPY', 'CH': 'CHF', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK',
    'PL': 'PLN', 'CZ': 'CZK', 'HU': 'HUF', 'RO': 'RON', 'BG': 'BGN',
    'HR': 'HRK', 'RU': 'RUB', 'TR': 'TRY', 'CN': 'CNY', 'HK': 'HKD',
    'SG': 'SGD', 'TW': 'TWD', 'KR': 'KRW', 'IN': 'INR', 'ID': 'IDR',
    'MY': 'MYR', 'PH': 'PHP', 'TH': 'THB', 'VN': 'VND', 'MX': 'MXN',
    'BR': 'BRL', 'AR': 'ARS', 'CL': 'CLP', 'CO': 'COP', 'PE': 'PEN',
    'UY': 'UYU', 'ZA': 'ZAR', 'AE': 'AED', 'SA': 'SAR', 'IL': 'ILS',
    'EG': 'EGP', 'NG': 'NGN', 'KE': 'KES', 'MA': 'MAD', 'PK': 'PKR',
    'BD': 'BDT', 'UA': 'UAH', 'KZ': 'KZT',
    // Euro zone countries
    'AT': 'EUR', 'BE': 'EUR', 'CY': 'EUR', 'EE': 'EUR', 'FI': 'EUR',
    'FR': 'EUR', 'DE': 'EUR', 'GR': 'EUR', 'IE': 'EUR', 'IT': 'EUR',
    'LV': 'EUR', 'LT': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'NL': 'EUR',
    'PT': 'EUR', 'SK': 'EUR', 'SI': 'EUR', 'ES': 'EUR'
  };

  isDayFirst(countryCode?: string | null): boolean {
    const normalized = (countryCode || 'US').toUpperCase();
    return this.dayFirstCountries.has(normalized);
  }

  isYearFirst(countryCode?: string | null): boolean {
    const normalized = (countryCode || 'US').toUpperCase();
    return this.yearFirstCountries.has(normalized);
  }

  getDateFormatsForCountry(countryCode?: string | null): CountryDateFormats {
    if (this.isYearFirst(countryCode)) {
      return {
        displayDateFormatAngular: 'yyyy-MM-dd',
        calendarDateFormat: 'YYYY-MM-DD',
        displayDateTimeFormatMoment: 'dddd, YYYY-MM-DD, h:mm A',
        shortDateTimeFormatMoment: 'YYYY-MM-DD h:mma',
        flatpickrDateFormat: 'Y-m-d'
      };
    }

    const useDayFirst = this.isDayFirst(countryCode);

    if (useDayFirst) {
      return {
        displayDateFormatAngular: 'dd/MM/yyyy',
        calendarDateFormat: 'DD/MM/YYYY',
        displayDateTimeFormatMoment: 'dddd, DD/MM/YYYY, h:mm A',
        shortDateTimeFormatMoment: 'DD/MM/YYYY h:mma',
        flatpickrDateFormat: 'd/m/Y'
      };
    }

    return {
      displayDateFormatAngular: 'M/d/yy',
      calendarDateFormat: 'MM/DD/YYYY',
      displayDateTimeFormatMoment: 'dddd, M/D/YYYY, h:mm A',
      shortDateTimeFormatMoment: 'MM/DD/YYYY h:mma',
      flatpickrDateFormat: 'm/d/Y'
    };
  }

  /**
   * Get the default currency code for a country
   * @param countryCode Two-letter country code
   * @returns Currency code (e.g., 'USD', 'EUR') or null if not found
   */
  getCurrencyForCountry(countryCode?: string | null): string | null {
    if (!countryCode) {
      return null;
    }
    const normalized = countryCode.toUpperCase();
    return this.countryCurrencyMap[normalized] || null;
  }
}


